import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail, Send, CheckCircle, Clock, Eye, MousePointer, Download, ChevronRight,
  User, Calendar, ArrowLeft, FileText, Settings, Play, Copy, AlertCircle
} from 'lucide-react';

type EmailStatus = 'Pending' | 'Sent' | 'Opened' | 'Clicked';

type EmailTemplate = {
  id: number;
  day: number;
  trigger: string;
  subject: string;
  body: string;
  status: EmailStatus;
};

type Variables = Record<string, string>;

const DEFAULT_VARIABLES: Variables = {
  '[First_Name]': 'John',
  '[Email]': 'john@example.com',
  '[Referral_Count]': '2',
  '[Friend_Name]': 'Sarah',
  '[Days_Left]': '20',
  '[Progress_Percentage]': '40',
  '[Login_Link]': 'https://portal.chrysalinks.co.za',
  '[Support_Email]': 'support@chrysalinks.co.za',
  '[Support_Phone]': '+27 81 714 2228',
  '[Script_Text]': 'Hey, I wanted to let you know about this amazing opportunity to get a FREE website...',
};

const EMAILS: EmailTemplate[] = [
  {
    id: 1, day: 0, trigger: 'Prospect creates account after accepting offer',
    subject: 'Welcome to Chrysalinks Group \u2014 Your FREE Website Journey Starts Now',
    status: 'Sent',
    body: `Hi [First_Name],

Welcome to Chrysalinks Group! You've taken the first step toward getting a FREE 1-pager website (worth R3,997) with lifetime hosting.

Here's how it works:
\u2705 Step 1: Refer 5 business owners who also want a FREE website
\u2705 Step 2: Complete a quick questionnaire (5 minutes)
\u2705 Step 3: We build your website (24-48 hours)

Your login details:
\u2022 Email: [Email]
\u2022 Portal: [Login_Link]

Your next step: Add your first referral using the referral form.

[Add Your First Referral]

Questions? Reply to this email or WhatsApp us at +27 81 714 2228.

Best regards,
The Chrysalinks Group Team
support@chrysalinks.co.za`,
  },
  {
    id: 2, day: 2, trigger: 'Automated (2 days after account creation)',
    subject: "How's Your Referral Journey Going?",
    status: 'Sent',
    body: `Hi [First_Name],

Just checking in \u2014 have you added your first referral yet?

Remember, you need 5 accepted referrals to unlock your FREE website. The sooner you start, the sooner you'll have your professional 1-pager live!

Quick reminder:
\u2022 Each referral = 1 step closer to your FREE website
\u2022 No limit on how many you can add
\u2022 Your referral form: [Login_Link]

[Add a Referral Now]

Need help? We're here for you:
\U0001f4e7 support@chrysalinks.co.za
\U0001f4f1 +27 81 714 2228

Best regards,
The Chrysalinks Group Team`,
  },
  {
    id: 3, day: 3, trigger: 'Automated (3 days after account creation)',
    subject: 'Your FREE Website is Waiting \u2014 Just 5 Referrals Away',
    status: 'Opened',
    body: `Hi [First_Name],

Did you know? Most people who get their FREE website complete their 5 referrals within the first week.

You're just 5 referrals away from:
\u2705 A professional 1-pager website (worth R3,997)
\u2705 Lifetime hosting (worth thousands more)
\u2705 1 design revision included

Start now \u2014 it only takes 2 minutes per referral:

[Start Referring Friends]

Best regards,
The Chrysalinks Group Team`,
  },
  {
    id: 4, day: 5, trigger: 'Automated (5 days after account creation)',
    subject: "You're Closer Than You Think",
    status: 'Opened',
    body: `Hi [First_Name],

Here's what successful referrers do:
\u2705 They share with business owners they know personally
\u2705 They explain the value: "FREE website worth R3,997"
\u2705 They make it easy \u2014 just share the referral link

Your referral link: [Login_Link]

Share it with 5 business owners who:
\u2022 Don't have a website yet
\u2022 Would benefit from a professional online presence
\u2022 Want to grow their customer base

[Share Your Referral Link]

Best regards,
The Chrysalinks Group Team`,
  },
  {
    id: 5, day: 7, trigger: 'Automated (7 days after account creation)',
    subject: 'Your Week 1 Progress \u2014 [Referral_Count]/5 Referrals',
    status: 'Pending',
    body: `Hi [First_Name],

Here's your progress this week:
\U0001f4ca Referrals accepted: [Referral_Count]/5
\U0001f3af Status: Needs Attention

You're making progress! Keep going \u2014 you're [Days_Left] referrals away from your FREE website.

Your referral form: [Login_Link]

Keep it up!

Best regards,
The Chrysalinks Group Team`,
  },
  {
    id: 6, day: 10, trigger: 'Automated (10 days after account creation)',
    subject: "Don't Miss Out \u2014 Your FREE Website Offer Expires Soon",
    status: 'Pending',
    body: `Hi [First_Name],

Quick heads up: Your FREE 1-pager website offer is available for a limited time.

You currently have [Referral_Count]/5 referrals accepted.

To complete your setup:
\u2705 Add [Days_Left] more referrals
\u2705 Complete your questionnaire
\u2705 Get your website live in 24-48 hours

Don't wait \u2014 complete your referrals today:

[Complete My Referrals]

Best regards,
The Chrysalinks Group Team`,
  },
  {
    id: 7, day: 14, trigger: 'Automated (14 days after account creation)',
    subject: 'Final Reminder \u2014 Your FREE Website is Almost Ready',
    status: 'Pending',
    body: `Hi [First_Name],

This is your final reminder about the FREE 1-pager website offer.

You have [Referral_Count]/5 referrals. Just [Days_Left] more to go!

What happens after you complete your referrals:
1. You get instant access to the questionnaire
2. We build your website (1-2 minutes with AI)
3. Admin reviews for quality (10-15 minutes)
4. You review and approve (1 revision included)
5. Your website goes LIVE!

[Complete My Referrals Now]

Best regards,
The Chrysalinks Group Team`,
  },
  {
    id: 8, day: 21, trigger: 'Automated (21 days after account creation)',
    subject: "Need Help? We're Here for You",
    status: 'Pending',
    body: `Hi [First_Name],

We noticed you haven't completed your 5 referrals yet. No worries \u2014 sometimes life gets busy!

If you need help:
\U0001f4de WhatsApp: +27 81 714 2228
\U0001f4e7 Email: [Support_Email]
\U0001f4ac Live Chat: Available on the portal

We can help you:
\u2022 Find the right people to refer
\u2022 Write the perfect referral message
\u2022 Troubleshoot any technical issues

[Get Help Now]

Best regards,
The Chrysalinks Group Team`,
  },
  {
    id: 9, day: 30, trigger: 'Automated (30 days after account creation)',
    subject: 'Your FREE Website \u2014 Still Available',
    status: 'Pending',
    body: `Hi [First_Name],

It's been a month since you joined Chrysalinks Group. Your FREE 1-pager website offer is still available!

Here's a quick recap:
\u2705 What you get: FREE 1-pager website (worth R3,997)
\u2705 What you need: 5 accepted referrals
\u2705 Timeline: 24-48 hours to go live

If you're ready to restart:
[Add My Referrals]

If you have questions:
[Schedule a Quick Chat]

Best regards,
The Chrysalinks Group Team`,
  },
];

const STATUS_ICONS: Record<EmailStatus, React.ReactNode> = {
  Pending: <Clock size={14} className="text-slate-400" />,
  Sent: <Send size={14} className="text-blue-500" />,
  Opened: <Eye size={14} className="text-emerald-500" />,
  Clicked: <MousePointer size={14} className="text-[#6B5E2B]" />,
};

const STATUS_COLORS: Record<EmailStatus, string> = {
  Pending: 'bg-slate-100 text-slate-600',
  Sent: 'bg-blue-100 text-blue-700',
  Opened: 'bg-emerald-100 text-emerald-700',
  Clicked: 'bg-[#6B5E2B]/10 text-[#6B5E2B]',
};

function substituteVariables(text: string, vars: Variables): string {
  let result = text;
  Object.entries(vars).forEach(([key, value]) => {
    result = result.split(key).join(value);
  });
  return result;
}

function formatEmailBody(body: string): string {
  return body
    .split('\n')
    .map((line) => {
      if (line.startsWith('[Add') || line.startsWith('[Start') || line.startsWith('[Share') ||
          line.startsWith('[Complete') || line.startsWith('[Get') || line.startsWith('[Schedule')) {
        const text = line.replace(/[\[\]]/g, '');
        return `<div style="margin:12px 0"><a href="#" style="display:inline-block;background:#6B5E2B;color:white;padding:10px 24px;border-radius:8px;text-decoration:none;font-weight:600">${text}</a></div>`;
      }
      if (line.trim() === '') return '<br/>';
      if (line.startsWith('\u2705') || line.startsWith('\u2022')) {
        return `<div style="padding:2px 0;color:#334155">${line}</div>`;
      }
      return `<div style="padding:2px 0;color:#334155">${line}</div>`;
    })
    .join('');
}

export const WelcomeEmailSequence: React.FC = () => {
  const [selectedId, setSelectedId] = useState(1);
  const [variables, setVariables] = useState<Variables>(DEFAULT_VARIABLES);
  const [showVariables, setShowVariables] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [emailStatuses, setEmailStatuses] = useState<EmailTemplate[]>(EMAILS);

  const selectedEmail = useMemo(() => emailStatuses.find((e) => e.id === selectedId) || emailStatuses[0], [selectedId, emailStatuses]);

  const renderedSubject = useMemo(() => substituteVariables(selectedEmail.subject, variables), [selectedEmail.subject, variables]);
  const renderedBody = useMemo(() => {
    const substituted = substituteVariables(selectedEmail.body, variables);
    return formatEmailBody(substituted);
  }, [selectedEmail.body, variables]);

  const handleSendTest = () => {
    setTestStatus('sending');
    setTimeout(() => {
      setTestStatus('sent');
      setTimeout(() => setTestStatus('idle'), 2000);
    }, 1500);
  };

  const handleExportJSON = () => {
    const exportData = emailStatuses.map(({ id, day, trigger, subject, body }) => ({ id, day, trigger, subject, body }));
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'welcome-email-sequence.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalSent = emailStatuses.filter((e) => e.status === 'Sent' || e.status === 'Opened' || e.status === 'Clicked').length;
  const totalOpened = emailStatuses.filter((e) => e.status === 'Opened' || e.status === 'Clicked').length;
  const totalClicked = emailStatuses.filter((e) => e.status === 'Clicked').length;

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Raleway, Arial, sans-serif' }}>
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#6B5E2B] flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#2C3E50]" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>
                Welcome Email Sequence
              </h1>
              <p className="text-xs text-slate-500">FREE 1-Pager Website Campaign &middot; 9 emails over 30 days</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowVariables(!showVariables)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 transition-colors">
              <Settings size={16} /> Variables
            </button>
            <button onClick={handleExportJSON}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 transition-colors">
              <Download size={16} /> Export JSON
            </button>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-6 text-sm">
          <span className="text-slate-500">Sequence Stats:</span>
          <span className="flex items-center gap-1.5"><Send size={14} className="text-blue-500" /> {totalSent} Sent</span>
          <span className="flex items-center gap-1.5"><Eye size={14} className="text-emerald-500" /> {totalOpened} Opened</span>
          <span className="flex items-center gap-1.5"><MousePointer size={14} className="text-[#6B5E2B]" /> {totalClicked} Clicked</span>
          <span className="flex items-center gap-1.5"><Clock size={14} className="text-slate-400" /> {emailStatuses.filter((e) => e.status === 'Pending').length} Pending</span>
        </div>
      </div>

      {/* Variables panel */}
      <AnimatePresence>
        {showVariables && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="bg-white border-b border-slate-200 overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 py-4">
              <h3 className="text-sm font-bold text-[#2C3E50] mb-3">Email Variables</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {Object.entries(variables).map(([key, val]) => (
                  <div key={key} className="flex items-center gap-2">
                    <code className="text-xs bg-slate-100 px-2 py-1 rounded text-[#6B5E2B] font-mono">{key}</code>
                    <input value={val} onChange={(e) => setVariables({ ...variables, [key]: e.target.value })}
                      className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:border-[#6B5E2B]" />
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-80 flex-shrink-0 space-y-1">
            {emailStatuses.map((email) => (
              <button key={email.id} onClick={() => setSelectedId(email.id)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  selectedId === email.id
                    ? 'border-[#6B5E2B] bg-[#6B5E2B]/5 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}>
                <div className="flex items-start justify-between mb-1">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${email.day === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    Day {email.day}
                  </span>
                  <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[email.status]}`}>
                    {STATUS_ICONS[email.status]} {email.status}
                  </span>
                </div>
                <p className="text-sm font-medium text-[#2C3E50] leading-tight line-clamp-2">{email.subject}</p>
                <p className="text-xs text-slate-400 mt-1">{email.trigger}</p>
              </button>
            ))}
          </div>

          {/* Email preview */}
          <div className="flex-1">
            <motion.div key={selectedId} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Email header */}
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-[#2C3E50] mb-1" style={{ fontFamily: 'Fairplay Display, Georgia, serif' }}>
                      {renderedSubject}
                    </h2>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1"><Calendar size={14} /> Day {selectedEmail.day}</span>
                      <span className="flex items-center gap-1"><Clock size={14} /> {selectedEmail.trigger}</span>
                    </div>
                  </div>
                  <span className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full ${STATUS_COLORS[selectedEmail.status]}`}>
                    {STATUS_ICONS[selectedEmail.status]} {selectedEmail.status}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#6B5E2B] flex items-center justify-center">
                      <User size={14} className="text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-700">Chrysalinks Group</p>
                      <p className="text-xs text-slate-400">welcome@chrysalinks.co.za</p>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-slate-300" />
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                      <User size={14} className="text-slate-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-700">{variables['[First_Name]']}</p>
                      <p className="text-xs text-slate-400">{variables['[Email]']}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email body */}
              <div className="p-6 max-w-2xl mx-auto">
                <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                  <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                    <span className="text-xs text-slate-400 ml-2">welcome@chrysalinks.co.za</span>
                  </div>
                  <div className="p-6 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: renderedBody }} />
                </div>
              </div>

              {/* Actions */}
              <div className="p-6 border-t border-slate-200 bg-slate-50">
                <div className="flex items-center gap-3">
                  <button onClick={handleSendTest} disabled={testStatus !== 'idle'}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-medium text-sm transition-all disabled:opacity-70"
                    style={{ background: '#6B5E2B' }}>
                    {testStatus === 'sending' ? (
                      <><Clock size={16} className="animate-spin" /> Sending...</>
                    ) : testStatus === 'sent' ? (
                      <><CheckCircle size={16} /> Sent!</>
                    ) : (
                      <><Play size={16} /> Send Test Email</>
                    )}
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-white transition-colors">
                    <Copy size={16} /> Copy Body
                  </button>
                  <span className="text-xs text-slate-400 ml-auto">
                    Reply-to: support@chrysalinks.co.za
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};
